/* neo-cart-drawer.js — Upcart-style slide-out cart drawer for ChewelryWorld.
   Vanilla JS, no dependencies. Driven by window.NeoCartDrawerConfig. */
(function () {
  "use strict";

  var CFG = window.NeoCartDrawerConfig;
  if (!CFG) return;
  var RT = CFG._runtime || {};
  var ROUTES = RT.routes || {};
  var MONEY_FMT = RT.moneyFormat || "${{amount}}";
  var SHOP_CURRENCY = RT.shopCurrency || "CAD";

  var root, drawer, overlay, els = {};
  var cart = null;
  var lastFocused = null;
  var busy = false;
  var recsLoadedFor = "";

  /* ---------------- money ----------------
     shop.money_format is the SHOP currency's format (CAD "${{amount}}"), but
     /cart.js reports the shopper's PRESENTMENT currency, and this store sells
     in 19 of them. Each has its own separators and symbol placement — Shopify
     renders CZK as "275,00 Kč" — so both the shop format and a generic Intl
     guess would print money in a shape the rest of the store never uses.
     Instead, derive the pattern from one worked example that Shopify itself
     formatted for THIS request (_runtime.moneySample = 1,234,567.89). */
  var MONEY_PATTERN = (function () {
    var s = RT.moneySample;
    if (!s) return null;
    var first = s.search(/\d/);
    var last = s.length - 1 - String(s).split("").reverse().join("").search(/\d/);
    if (first === -1) return null;

    var num = s.slice(first, last + 1);
    var digits = num.replace(/\D/g, "");
    // 123456789 cents is 1234567.89 — 9 digits back means the format keeps its
    // decimals, 7 means it rounds them away (JPY, KRW).
    var decimals = digits.length >= 9 ? 2 : 0;
    var seps = num.replace(/\d/g, "");
    if (!seps.length) return { prefix: s.slice(0, first), suffix: s.slice(last + 1), thousands: "", decimal: "", decimals: decimals };

    return {
      prefix: s.slice(0, first),
      suffix: s.slice(last + 1),
      decimal: decimals ? seps.charAt(seps.length - 1) : "",
      thousands: seps.charAt(0),
      decimals: decimals
    };
  })();

  function formatMoney(cents) {
    if (typeof cents === "string") cents = parseInt(cents.replace(/[^0-9-]/g, ""), 10);
    if (cents == null || isNaN(cents)) cents = 0;

    if (MONEY_PATTERN) {
      var p = MONEY_PATTERN;
      var neg = cents < 0;
      var abs = Math.abs(cents);
      var n = p.decimals ? (abs / 100).toFixed(2) : String(Math.round(abs / 100));
      var bits = n.split(".");
      var whole = bits[0].replace(/\B(?=(\d{3})+(?!\d))/g, p.thousands);
      var frac = p.decimals && bits[1] ? p.decimal + bits[1] : "";
      return (neg ? "-" : "") + p.prefix + whole + frac + p.suffix;
    }

    var m = MONEY_FMT.match(/\{\{\s*(\w+)\s*\}\}/);
    var token = m ? m[1] : "amount";
    function fmt(number, precision, thousands, decimal) {
      thousands = thousands == null ? "," : thousands;
      decimal = decimal == null ? "." : decimal;
      var n = (number / 100).toFixed(precision);
      var parts = n.split(".");
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands);
      return dollars + (parts[1] ? decimal + parts[1] : "");
    }
    var value;
    switch (token) {
      case "amount_no_decimals": value = fmt(cents, 0); break;
      case "amount_with_comma_separator": value = fmt(cents, 2, ".", ","); break;
      case "amount_no_decimals_with_comma_separator": value = fmt(cents, 0, ".", ","); break;
      case "amount_with_space_separator": value = fmt(cents, 2, " ", "."); break;
      case "amount_with_apostrophe_separator": value = fmt(cents, 2, "'", "."); break;
      default: value = fmt(cents, 2);
    }
    return MONEY_FMT.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  /* ---------------- design vars ---------------- */
  function applyDesign() {
    var d = CFG.design || {};
    var c = d.colors || {};
    var s = root.style;
    if (c.background) s.setProperty("--neo-bg", c.background);
    if (c.text) s.setProperty("--neo-text", c.text);
    if (c.muted) s.setProperty("--neo-muted", c.muted);
    if (c.border) s.setProperty("--neo-border", c.border);
    if (c.primary) s.setProperty("--neo-primary", c.primary);
    if (c.primaryText) s.setProperty("--neo-primary-text", c.primaryText);
    if (c.accent) s.setProperty("--neo-accent", c.accent);
    if (c.success) s.setProperty("--neo-success", c.success);
    if (d.cornerRadius != null) s.setProperty("--neo-radius", d.cornerRadius + "px");
    if (d.cartWidthDesktop) s.setProperty("--neo-width", d.cartWidthDesktop + "px");
  }

  /* ---------------- fetch cart ---------------- */
  function getCart() {
    return fetch((ROUTES.cart || "/cart") + ".js", { headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); });
  }

  function protVariantId() {
    var a = (CFG.addOns && CFG.addOns.shippingProtection) || {};
    return a.enabled ? a.variantId : 0;
  }
  function protProductId() {
    var a = (CFG.addOns && CFG.addOns.shippingProtection) || {};
    return a.enabled ? a.productId : 0;
  }

  /* ---------------- render ---------------- */
  function render() {
    if (!cart) return;
    var protVar = protVariantId();

    /* header: "Cart • N" live count (add-ons excluded, per Upcart) */
    var realCount = cart.items.reduce(function (sum, it) {
      return it.variant_id === protVar ? sum : sum + it.quantity;
    }, 0);
    if (els.title) els.title.textContent = "Cart • " + realCount;

    /* reward bar (exclude protection from spend) */
    renderReward(protVar);
    renderNudge(protVar);

    /* items */
    els.items.innerHTML = "";
    var realItems = cart.items.filter(function (it) { return it.variant_id !== protVar; });
    if (cart.items.length === 0) {
      els.empty.hidden = false;
      els.foot.hidden = true;
      els.addon.hidden = true;
      els.recs.hidden = true;
      setAria();
      return;
    }
    els.empty.hidden = true;
    els.foot.hidden = false;

    cart.items.forEach(function (it) {
      if (it.variant_id === protVar) return; // protection rendered as toggle, not a line
      els.items.appendChild(itemRow(it));
    });

    renderAddon(protVar);
    renderFooter(protVar);
    renderRecs();
    setAria();
  }

  function renderNudge(protVar) {
    var n = CFG.multiBuyNudge || {};
    if (!n.enabled || !els.nudge) { if (els.nudge) els.nudge.hidden = true; return; }
    var qty = cart.items.reduce(function (sum, it) {
      return it.variant_id === protVar ? sum : sum + it.quantity;
    }, 0);
    if (qty === 0) { els.nudge.hidden = true; return; }
    els.nudge.hidden = false;
    var need = n.needQty || 3;
    var met = qty >= need;
    els.nudge.classList.toggle("is-met", met);
    els.nudgeMsg.textContent = met
      ? (n.messageMet || "")
      : (n.messageBelow || "").replace("[more]", need - qty);
  }

  /* The free-shipping condition is enforced by an automatic discount against
     the SHOP-currency (CAD) subtotal, but /cart.js reports the presentment
     currency. Convert with Shopify's own rate — the same one the announcement
     bar uses — and round up on the identical tidy-step ladder, so the drawer
     and the banner can never quote two different numbers to the same shopper.
     Rounding UP also guarantees anyone who clears the bar clears the real
     CAD condition, never the reverse. */
  function rewardThresholdCents(r) {
    if (r.thresholdCad == null) return r.threshold || 0; /* legacy: literal cents */
    var cad = parseFloat(r.thresholdCad);
    if (!isFinite(cad)) return 0;

    var cur = window.Shopify && window.Shopify.currency;
    var active = cur && cur.active;
    var rate = cur ? parseFloat(cur.rate) : NaN;
    if (!active || active === SHOP_CURRENCY || !isFinite(rate) || rate <= 0) {
      return Math.round(cad * 100);
    }

    var raw = cad * rate;
    var step = raw < 100 ? 5 : raw < 1000 ? 10 : raw < 10000 ? 100 : 1000;
    var amount = Math.ceil(raw / step) * step;
    if (amount < raw * 1.01) amount += step;
    return Math.round(amount * 100);
  }

  function renderReward(protVar) {
    var r = CFG.tieredRewards || {};
    if (!r.enabled) { els.reward.hidden = true; return; }
    els.reward.hidden = false;
    var spent = cart.items.reduce(function (sum, it) {
      return it.variant_id === protVar ? sum : sum + it.final_line_price;
    }, 0);
    var threshold = rewardThresholdCents(r);
    var pct = threshold > 0 ? Math.min(100, (spent / threshold) * 100) : 100;
    var met = spent >= threshold;
    els.reward.classList.toggle("is-met", met);
    if (met) {
      els.rewardMsg.textContent = r.messageMet || "Free shipping unlocked!";
    } else {
      var remaining = formatMoney(threshold - spent);
      els.rewardMsg.textContent = (r.messageBelow || "You're [remaining] away from free shipping!")
        .replace("[remaining]", remaining);
    }
    els.rewardFill.style.width = pct + "%";
  }

  function itemRow(it) {
    var row = document.createElement("div");
    row.className = "neo-cd__item";
    row.setAttribute("role", "listitem");
    row.dataset.key = it.key;

    var img = it.image ? it.image.replace(/(\.[a-z]+)(\?|$)/i, "_200x$1$2") : "";
    var variant = "";
    if ((CFG.cartItems || {}).showVariant && it.variant_title && it.variant_title !== "Default Title") {
      variant = '<p class="neo-cd__item-variant">' + esc(it.variant_title) + "</p>";
    }
    var qtyHtml = "";
    if ((CFG.cartItems || {}).allowQuantityChange) {
      qtyHtml =
        '<div class="neo-cd__qty">' +
        '<button type="button" data-neo-dec aria-label="Decrease quantity">−</button>' +
        '<span class="neo-cd__qty-val">' + it.quantity + "</span>" +
        '<button type="button" data-neo-inc aria-label="Increase quantity">+</button>' +
        "</div>";
    } else {
      qtyHtml = '<span class="neo-cd__item-variant">Qty: ' + it.quantity + "</span>";
    }
    var removeHtml = (CFG.cartItems || {}).allowRemove
      ? '<button type="button" class="neo-cd__remove" data-neo-remove aria-label="Remove item">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" ' +
        'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
        '<path d="M3 6h18"/><path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2"/>' +
        '<path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/>' +
        '<path d="M10 11v6M14 11v6"/></svg></button>'
      : "";

    row.innerHTML =
      (img ? '<img class="neo-cd__item-img" src="' + img + '" alt="" loading="lazy">' : '<div class="neo-cd__item-img"></div>') +
      '<div class="neo-cd__item-main">' +
      '<a class="neo-cd__item-title" href="' + (it.url || "#") + '">' + esc(it.product_title) + "</a>" +
      variant +
      '<p class="neo-cd__item-price">' + formatMoney(it.final_line_price) + "</p>" +
      qtyHtml +
      "</div>" +
      '<div class="neo-cd__item-right">' + removeHtml + "</div>";
    return row;
  }

  function renderAddon(protVar) {
    var a = (CFG.addOns && CFG.addOns.shippingProtection) || {};
    if (!a.enabled) { els.addon.hidden = true; return; }
    els.addon.hidden = false;
    var on = cart.items.some(function (it) { return it.variant_id === a.variantId; });
    els.addon.innerHTML =
      '<div class="neo-cd__addon-icon">' + shieldSvg() + "</div>" +
      '<div class="neo-cd__addon-text">' +
      '<div class="neo-cd__addon-title">' + esc(a.title) + ' <span>' + formatMoney(a.price) + "</span></div>" +
      '<div class="neo-cd__addon-desc">' + esc(a.description) + "</div>" +
      "</div>" +
      '<button type="button" class="neo-cd__switch' + (on ? " is-on" : "") +
      '" data-neo-prot role="switch" aria-checked="' + on + '" aria-label="Toggle shipping protection"></button>';
  }

  function renderFooter(protVar) {
    var cs = CFG.cartSummary || {};
    /* no subtotal row — the total lives in the checkout button (Upcart).
       The padlock is decorative and sits inside the button, so the label is
       built as markup rather than textContent. Everything interpolated here is
       either config copy or formatMoney output, both escaped. */
    els.checkout.innerHTML =
      (cs.showLockIcon === false ? "" : lockSvg()) +
      '<span class="neo-cd__checkout-label">' +
      esc(cs.checkoutText || "Checkout") + " • " + esc(formatMoney(cart.total_price)) +
      "</span>";

    /* trust badges */
    var tb = CFG.trustBadges || {};
    if (tb.enabled && tb.items && tb.items.length) {
      els.trust.hidden = false;
      els.trust.innerHTML = tb.items.map(function (b) {
        return '<span class="neo-cd__trust-item">' + trustIconSvg(b.icon) + esc(b.text) + "</span>";
      }).join("");
    } else { els.trust.hidden = true; }

    /* payment icons: rendered statically in liquid (fixed Upcart order) */
    els.payicons.hidden = cs.showPaymentIcons === false;
  }

  /* ---------------- recommendations ---------------- */
  function renderRecs() {
    var rc = CFG.recommendations || {};
    if (!rc.enabled || !cart.items.length) { els.recs.hidden = true; return; }
    var seed = cart.items.find(function (it) { return it.variant_id !== protVariantId(); });
    if (!seed) { els.recs.hidden = true; return; }
    if (recsLoadedFor === seed.product_id + ":" + cart.item_count) return; // already rendered for this state
    recsLoadedFor = seed.product_id + ":" + cart.item_count;

    var url = ROUTES.recommendations + ".json?product_id=" + seed.product_id +
      "&limit=" + (rc.limit || 6) + "&intent=related";
    fetch(url, { headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var list = normalizeRecs(data.products || [], true);
        list = filterRecs(list);
        if (list.length) { paintRecs(list, rc.title); return; }
        return loadFallback(rc);
      })
      .catch(function () { return loadFallback(rc); });
  }

  function loadFallback(rc) {
    return fetch("/products.json?limit=12", { headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var list = normalizeRecs(data.products || [], false);
        list = filterRecs(list);
        if (list.length) paintRecs(list.slice(0, rc.limit || 6), rc.title);
        else els.recs.hidden = true;
      })
      .catch(function () { els.recs.hidden = true; });
  }

  // priceInCents=true for /recommendations (product.js shape), false for /products.json (REST shape)
  function normalizeRecs(products, priceInCents) {
    return products.map(function (p) {
      var img = "";
      if (typeof p.featured_image === "string") img = p.featured_image;
      else if (p.featured_image && p.featured_image.src) img = p.featured_image.src;
      else if (p.images && p.images.length) img = (typeof p.images[0] === "string") ? p.images[0] : p.images[0].src;
      var variants = (p.variants || []).map(function (v) {
        var price = priceInCents ? v.price : Math.round(parseFloat(v.price) * 100);
        var avail = priceInCents ? v.available : (v.available !== false);
        return { id: v.id, title: v.title, price: price, available: avail };
      });
      return {
        id: p.id,
        title: p.title,
        handle: p.handle,
        url: "/products/" + p.handle,
        image: img,
        variants: variants,
        minPrice: variants.length ? Math.min.apply(null, variants.map(function (v) { return v.price; })) : 0
      };
    });
  }

  function filterRecs(list) {
    var inCart = {};
    cart.items.forEach(function (it) { inCart[it.product_id] = true; });
    var protPid = protProductId();
    return list.filter(function (p) {
      if (inCart[p.id]) return false;
      if (protPid && p.id === protPid) return false;
      if (!p.variants.some(function (v) { return v.available; })) return false;
      return true;
    });
  }

  function paintRecs(list, title) {
    els.recs.hidden = false;
    els.recsTitle.textContent = title || "You'll love these";
    els.recsTrack.innerHTML = "";
    list.forEach(function (p) {
      var card = document.createElement("div");
      card.className = "neo-cd__rec";
      var multi = p.variants.length > 1;
      var opts = multi ? p.variants.filter(function (v) { return v.available; }).map(function (v) {
        return '<option value="' + v.id + '">' + esc(v.title) + "</option>";
      }).join("") : "";
      var firstAvail = p.variants.filter(function (v) { return v.available; })[0] || p.variants[0];
      /* horizontal card: image left, title/price/controls right (Upcart) */
      card.innerHTML =
        '<a href="' + p.url + '"><img class="neo-cd__rec-img" src="' +
        (p.image ? p.image.replace(/(\.[a-z]+)(\?|$)/i, "_200x$1$2") : "") + '" alt="" loading="lazy"></a>' +
        '<div class="neo-cd__rec-main">' +
        '<a class="neo-cd__rec-title" href="' + p.url + '">' + esc(p.title) + "</a>" +
        '<div class="neo-cd__rec-price">' + formatMoney(p.minPrice) + "</div>" +
        '<div class="neo-cd__rec-row">' +
        (multi ? '<select class="neo-cd__rec-var">' + opts + "</select>" : "") +
        '<button type="button" class="neo-cd__rec-add" data-id="' + (firstAvail ? firstAvail.id : "") + '">Add</button>' +
        "</div></div>";
      els.recsTrack.appendChild(card);
    });
    els.recsTrack.scrollLeft = 0;
    var single = list.length <= 1;
    if (els.recsPrev) els.recsPrev.hidden = single;
    if (els.recsNext) els.recsNext.hidden = single;
  }

  function scrollRecs(dir) {
    var w = els.recsTrack.clientWidth;
    var max = els.recsTrack.scrollWidth - w;
    var next = els.recsTrack.scrollLeft + dir * w;
    /* wrap around at the ends so the arrows always do something */
    if (next > max + 4) next = 0;
    else if (next < -4) next = max;
    els.recsTrack.scrollTo({ left: next, behavior: "smooth" });
  }

  /* ---------------- cart mutations ---------------- */
  function addToCart(items) {
    return fetch(ROUTES.cartAdd || "/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: items })
    }).then(function (r) { return r.json(); });
  }

  function changeLine(key, quantity) {
    return fetch(ROUTES.cartChange || "/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: key, quantity: quantity })
    }).then(function (r) { return r.json(); });
  }

  function refreshAndRender() {
    return getCart().then(function (c) { cart = c; recsLoadedFor = ""; render(); updateBubble(); });
  }

  function updateBubble() {
    var b = document.querySelector("#cart-icon-bubble .cart-count-bubble, .cart-count-bubble");
    if (b && cart) {
      var n = b.querySelector("span[aria-hidden], span:first-child") || b.firstElementChild;
      if (n) n.textContent = cart.item_count;
    }
  }

  /* ---------------- events ---------------- */
  function onBodyClick(e) {
    var t = e.target;
    if (t.closest("[data-neo-cd-close]")) { close(); return; }

    var row = t.closest(".neo-cd__item");
    if (row) {
      var key = row.dataset.key;
      var item = cart.items.filter(function (it) { return it.key === key; })[0];
      if (!item) return;
      if (t.closest("[data-neo-inc]")) return void mutate(row, changeLine(key, item.quantity + 1));
      if (t.closest("[data-neo-dec]")) return void mutate(row, changeLine(key, item.quantity - 1));
      if (t.closest("[data-neo-remove]")) return void mutate(row, changeLine(key, 0));
    }

    var protBtn = t.closest("[data-neo-prot]");
    if (protBtn) return void toggleProtection(protBtn);

    var addBtn = t.closest(".neo-cd__rec-add");
    if (addBtn) return void addRec(addBtn);

    if (t.closest(".neo-cd__recs-prev")) return void scrollRecs(-1);
    if (t.closest(".neo-cd__recs-next")) return void scrollRecs(1);
  }

  function mutate(row, promise) {
    if (busy) return;
    busy = true;
    if (row) row.classList.add("is-busy");
    promise.then(function (c) { cart = c; recsLoadedFor = ""; render(); updateBubble(); })
      .catch(function () {})
      .then(function () { busy = false; });
  }

  function toggleProtection(btn) {
    if (busy) return;
    var a = CFG.addOns.shippingProtection;
    var on = btn.classList.contains("is-on");
    btn.classList.add("is-busy");
    busy = true;
    var line = cart.items.filter(function (it) { return it.variant_id === a.variantId; })[0];
    var p = on
      ? (line ? changeLine(line.key, 0) : Promise.resolve(cart))
      : addToCart([{ id: a.variantId, quantity: 1 }]).then(function () { return getCart(); });
    p.then(function (c) { cart = c; recsLoadedFor = ""; render(); updateBubble(); })
      .catch(function () {})
      .then(function () { busy = false; });
  }

  function addRec(btn) {
    if (busy) return;
    var card = btn.closest(".neo-cd__rec");
    var sel = card.querySelector(".neo-cd__rec-var");
    var id = sel ? sel.value : btn.dataset.id;
    if (!id) return;
    busy = true;
    btn.disabled = true;
    btn.textContent = "Adding…";
    addToCart([{ id: parseInt(id, 10), quantity: 1 }])
      .then(function () { return refreshAndRender(); })
      .catch(function () {})
      .then(function () { busy = false; });
  }

  /* ---------------- add-to-cart interception ---------------- */
  function onSubmitCapture(e) {
    var form = e.target;
    if (!form || form.tagName !== "FORM") return;
    var action = form.getAttribute("action") || "";
    if (action.indexOf("/cart/add") === -1) return;
    e.preventDefault();
    e.stopImmediatePropagation();

    if (cart) render();
    open(); // slide out instantly; contents update when the add lands

    var fd = new FormData(form);
    var btn = form.querySelector('[type="submit"]');
    var oldLabel;
    if (btn) { oldLabel = btn.getAttribute("aria-busy"); btn.setAttribute("aria-busy", "true"); }

    fetch(ROUTES.cartAdd || "/cart/add.js", {
      method: "POST",
      headers: { Accept: "application/json" },
      body: fd
    })
      .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, body: j }; }); })
      .then(function (res) {
        if (btn) btn.removeAttribute("aria-busy");
        if (!res.ok) { window.location = ROUTES.cart || "/cart"; return; }
        return refreshAndRender();
      })
      .catch(function () { if (btn) btn.removeAttribute("aria-busy"); window.location = ROUTES.cart || "/cart"; });
  }

  function onHeaderCartClick(e) {
    var link = e.target.closest('#cart-icon-bubble, a[href$="/cart"], a[href*="/cart?"]');
    if (!link) return;
    // only hijack header/nav cart links, not in-page "view cart" content
    e.preventDefault();
    if (cart) render();
    open();
  }

  /* ---------------- open / close + focus trap ---------------- */
  function open() {
    if (!cart) refreshAndRender(); // fetch in background — slide out instantly
    lastFocused = document.activeElement;
    root.classList.add("is-open");
    root.setAttribute("aria-hidden", "false");
    document.documentElement.classList.add("neo-cd-lock");
    document.body.classList.add("neo-cd-lock");
    document.addEventListener("keydown", onKeydown);
    setTimeout(function () { drawer.focus(); }, 30);
  }
  function close() {
    root.classList.remove("is-open");
    root.setAttribute("aria-hidden", "true");
    document.documentElement.classList.remove("neo-cd-lock");
    document.body.classList.remove("neo-cd-lock");
    document.removeEventListener("keydown", onKeydown);
    if (lastFocused && lastFocused.focus) lastFocused.focus();
  }
  function setAria() {
    root.setAttribute("aria-hidden", root.classList.contains("is-open") ? "false" : "true");
  }
  function onKeydown(e) {
    if (e.key === "Escape") { close(); return; }
    if (e.key !== "Tab") return;
    var focusables = drawer.querySelectorAll(
      'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    var list = Array.prototype.filter.call(focusables, function (n) { return n.offsetParent !== null; });
    if (!list.length) return;
    var first = list[0], last = list[list.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  /* ---------------- checkout ---------------- */
  function onCheckout() {
    if (!cart || !cart.item_count) return;
    els.checkout.disabled = true;
    var dc = CFG.discountCode || {};
    if (dc.enabled && dc.stored) {
      window.location = "/discount/" + encodeURIComponent(dc.stored) + "?redirect=/checkout";
    } else {
      window.location = "/checkout";
    }
  }

  /* Back button from /checkout restores this page from the bfcache with the
     button still disabled — re-enable it whenever the page is shown. */
  window.addEventListener("pageshow", function () {
    if (els && els.checkout) els.checkout.disabled = false;
  });

  /* ---------------- helpers ---------------- */
  function esc(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  }
  /* Padlock for the checkout button. Single-colour, inherits currentColor, so
     it works on the black button without a second asset. */
  function lockSvg() {
    return '<svg class="neo-cd__checkout-lock" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
      '<rect x="4" y="10.5" width="16" height="11" rx="2"/>' +
      '<path d="M8 10.5V7a4 4 0 0 1 8 0v3.5"/></svg>';
  }

  /* Trust-badge icons. Line icons only, currentColor, so they read as one set
     with the muted badge text instead of competing with the payment icons. */
  function trustIconSvg(name) {
    var paths = {
      lock: '<rect x="4" y="10.5" width="16" height="11" rx="2"/><path d="M8 10.5V7a4 4 0 0 1 8 0v3.5"/>',
      /* arrow curving back on itself — returns */
      return: '<path d="M3 10h12a5 5 0 0 1 0 10h-6"/><path d="M7 6l-4 4 4 4"/>',
      pin: '<path d="M12 21s7-5.5 7-11a7 7 0 1 0-14 0c0 5.5 7 11 7 11z"/><circle cx="12" cy="10" r="2.5"/>',
      truck: '<path d="M3 7h11v9H3z"/><path d="M14 10h4l3 3v3h-7z"/><circle cx="7" cy="18" r="1.8"/><circle cx="17" cy="18" r="1.8"/>'
    };
    var d = paths[name];
    if (!d) return "";
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" ' +
      'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' + d + "</svg>";
  }

  function shieldSvg() {
    /* colorful blue/teal illustration-style shield (Upcart look) */
    return '<svg viewBox="0 0 24 24" aria-hidden="true">' +
      '<defs><linearGradient id="neo-cd-shield-grad" x1="0" y1="0" x2="1" y2="1">' +
      '<stop offset="0" stop-color="#38bdf8"/><stop offset="1" stop-color="#0d9488"/>' +
      "</linearGradient></defs>" +
      '<path d="M12 2l8 3v6c0 5-3.5 8.5-8 11-4.5-2.5-8-6-8-11V5l8-3z" fill="url(#neo-cd-shield-grad)"/>' +
      '<path d="M12 2l8 3v6c0 5-3.5 8.5-8 11V2z" fill="#0ea5e9" opacity="0.35"/>' +
      '<path d="M8.7 12.1l2.3 2.3 4.5-4.5" fill="none" stroke="#ffffff" stroke-width="2" ' +
      'stroke-linecap="round" stroke-linejoin="round"/></svg>';
  }

  /* ---------------- init ---------------- */
  function init() {
    root = document.getElementById("neo-cd-root");
    if (!root) return;
    drawer = root.querySelector(".neo-cd__drawer");
    overlay = root.querySelector(".neo-cd__overlay");
    els = {
      title: root.querySelector(".neo-cd__title"),
      reward: root.querySelector(".neo-cd__reward"),
      nudge: root.querySelector(".neo-cd__nudge"),
      nudgeMsg: root.querySelector(".neo-cd__nudge-msg"),
      rewardMsg: root.querySelector(".neo-cd__reward-msg"),
      rewardFill: root.querySelector(".neo-cd__reward-fill"),
      items: root.querySelector(".neo-cd__items"),
      empty: root.querySelector(".neo-cd__empty"),
      addon: root.querySelector(".neo-cd__addon"),
      recs: root.querySelector(".neo-cd__recs"),
      recsTitle: root.querySelector(".neo-cd__recs-title"),
      recsTrack: root.querySelector(".neo-cd__recs-track"),
      recsPrev: root.querySelector(".neo-cd__recs-prev"),
      recsNext: root.querySelector(".neo-cd__recs-next"),
      foot: root.querySelector(".neo-cd__foot"),
      trust: root.querySelector(".neo-cd__trust"),
      checkout: root.querySelector(".neo-cd__checkout"),
      payicons: root.querySelector(".neo-cd__payicons")
    };
    applyDesign();

    root.addEventListener("click", onBodyClick);
    els.checkout.addEventListener("click", onCheckout);
    document.addEventListener("submit", onSubmitCapture, true); // capture: beat theme handlers
    document.addEventListener("click", onHeaderCartClick, true);

    // prime cart state (non-blocking)
    getCart().then(function (c) { cart = c; updateBubble(); }).catch(function () {});
    window.NeoCartDrawer = { open: open, close: close, refresh: refreshAndRender };
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else { init(); }
})();
